/// <reference path="../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../dispatcher';
import { AppTranscriptCardChangeParameter, ITranscriptData } from '../components';
import { ITranscriptionSegment, ITranscription, ITranscriptionManager } from '../../backend/interfaces/ITranscriptionManager';
import { SetFailureStateAndRouteToFailure, RevertAllStates, FailureContinuation } from './FailureActions';
import * as DocumentStorage from '../../backend/office-js/DocumentStorage';
import * as AddToDocument from '../../backend/office-js/AddTranscriptionToDocument';
import { TranscriptionRoutePathNames } from '../routes';
import { CreateTranscriptionManager } from '../../backend/TranscriptionManagerFactory';
import { ITranscriptionSourceFile } from '../../backend/interfaces/ITranscriptionSourceFileProvider';
import { GetStoredTranscription } from '../../backend/office-js/DocumentStorage';
import { DocumentStorageError, DocumentStorageErrors } from '../../backend/office-js/DocumentStorageErrors';
import { LogEventNames } from '../../backend/interfaces/ITelemetryLogger';
import { LogError, ShouldLogErrorAsWarning } from '../../utils/ErrorTelemetry';
import { ErrorStringFromException } from '../../utils/utils';
import { InitializeAudioBackup, AudioBackupAndRecoveryStatus } from './RecoveringActions';
import { NavigateToLandingPageAfterAudioBackupCheck } from './LandingActions';
import { BootError, BootErrors } from '../BootErrors';
import { AudioBackupError, AudioBackupErrors } from '../../backend/audioBackup/AudioBackupErrors';

export enum AddToDocumentMenuOption {
  JustText = 'JustText',
  WithSpeakers = 'WithSpeakers',
  WithTimestamps = 'WithTimestamps',
  WithSpeakersAndTimestamps ='WithSpeakersAndTimestamps'
}

export const TranscribingActionTypes = {
  SetEditingIndexAction: 'TRANSCRIBING_SET_EDITING_INDEX',
  SetFocusedIndexAction: 'TRANSCRIBING_SET_FOCUSED_INDEX',
  SetHighlightedIndexAction: 'TRANSCRIBING_SET_HIGHLIGHTING_INDEX',
  SetSourceFileAction: 'TRANSCRIBING_SET_SOURCE_FILE',
  SetTranscriptionDataAction: 'TRANSCRIBING_SET_TRANSCRIPTION_DATA',
  SetTranscriptionManager: 'TRANSCRIBING_SET_TRANSCRIPTION_MANAGER',
  SetAudioPlayingFlag: 'TRANSCRIBING_SET_AUDIO_PLAYING_FLAG',
  RevertStateAction: 'TRANSCRIBING_REVERT_STATE',
  SetTeachingUIClosedFlag: 'TRANSCRIBING_TEACHING_UI_CLOSED_FLAG',
  SetShowStartOverConfirmDialog: 'TRANSCRIBING_SET_SHOW_START_OVER_CONFIRMED_DIALOG'
};

function GetFailureReasonForTranscribingError(error: any) {
  if (error instanceof BootError && error.name == BootErrors.IndexeddbFailedToOpen
    || error instanceof AudioBackupError && error.name == AudioBackupErrors.UnsupportedBrowser) {
    return TranscriptionStrings.CookieSettingsFailureReason;
  }

  return TranscriptionStrings.UnknownInitFailureReason;
}

function getAddToDocumentOptionsFromMenuOption(menuOption: AddToDocumentMenuOption) {
  switch (menuOption) {
    case AddToDocumentMenuOption.JustText:
      return { withTimestamps: false, withSpeakers: false }
    case AddToDocumentMenuOption.WithSpeakers:
      return { withTimestamps: false, withSpeakers: true }
    case AddToDocumentMenuOption.WithTimestamps:
      return { withTimestamps: true, withSpeakers: false }
    default:
      return { withTimestamps: true, withSpeakers: true }
  }
}

export function SetEditingMode(index?: number) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetEditingIndexAction,
    payload: index
  });
}

export function SetFocusedIndex(index?: number) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetFocusedIndexAction,
    payload: index
  });
}

export function SetTeachingUIClosedFlag(flag: boolean) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetTeachingUIClosedFlag,
    payload: flag
  });
}

export function SetAudioPlaying(flag: boolean) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetAudioPlayingFlag,
    payload: flag
  });
}

export function SetShowStartOverConfirmDialog(flag: boolean) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetShowStartOverConfirmDialog,
    payload: flag
  });
}

export function GetSpeakerName(speakerId: number | undefined | null, speakerNames: (string | null)[] | undefined) {
  if (speakerId == null)
    return TranscriptionStrings.DefaultNameForSpeaker;

  if (speakerNames == null || speakerNames[speakerId] == null)
    return `${TranscriptionStrings.DefaultNameForSpeaker} ${speakerId + 1}`;

  return speakerNames[speakerId];
}

export function InitializeTranscriptionData(history: any) {
  try {
    const transcriptionManager: ITranscriptionManager = CreateTranscriptionManager(GetStoredTranscription());
    SetTranscriptionManager(transcriptionManager);

    const transcription: ITranscription = transcriptionManager.GetTranscription();
    const segments: ITranscriptionSegment[] = transcription.transcriptSegments;
    const transcriptionData: ITranscriptData[] = segments.map(
      (transcriptionSegment: ITranscriptionSegment, index: number) => {
        // Due to the lack of availability of the speaker details from the OneDrive transcription API we keep the speaker field static
        return {
          id: index,
          text: transcriptionSegment.text,
          timestamp: transcriptionSegment.start,
          speaker: GetSpeakerName(transcriptionSegment.speakerId, transcription.speakerNames)
        };
      }
    );

    Dispatcher.dispatch({
      type: TranscribingActionTypes.SetTranscriptionDataAction,
      payload: transcriptionData
    });
  } catch (error) {
    let continuation:FailureContinuation = FailureContinuation.None;
    if (error instanceof DocumentStorageError && error.name == DocumentStorageErrors.CorruptTranscriptSource) {
      continuation = FailureContinuation.StartOver;
    }

    handleFailure(
      LogEventNames.DocumentStorage,
      GetFailureReasonForTranscribingError(error), 
      continuation /*failureContinuation*/, 
      'InitializeTranscriptionData threw error: ' + ErrorStringFromException(error),
      history);
  }
}

export function SetHighlightedIndex(index: number) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetHighlightedIndexAction,
    payload: index
  });
}

export async function DeleteStoredTranscriptionAndRouteToLanding(history: any) {
  await DocumentStorage.DeleteStoredTranscription();
  RevertAllStates();

  // We want to create a transcription connection and audio backup here to avoid paying the initialization price later, so it improves the "time to input" metric.
  const backupAndState: AudioBackupAndRecoveryStatus = await InitializeAudioBackup();
  if (backupAndState.needsRecovery) {
    // in case of recovery, the transcription connection is created later from inside the Recovering page
    history.replace(TranscriptionRoutePathNames.Recovering, {audioBackup: backupAndState.audioBackup});
  } else {
    NavigateToLandingPageAfterAudioBackupCheck(backupAndState, history);
  }
}

export async function StartOverClicked(history: any) {
  try {
    await DeleteStoredTranscriptionAndRouteToLanding(history);
  } catch (error) {
    handleFailure(
      ShouldLogErrorAsWarning(error) ? LogEventNames.Warning: LogEventNames.DocumentStorage,
      GetFailureReasonForTranscribingError(error), 
      FailureContinuation.None /*failureContinuation*/, 
      'Starting over threw exception: ' + ErrorStringFromException(error),
      history);
  }
}

export async function TranscriptionDataModified(transcriptionManager: ITranscriptionManager, data: AppTranscriptCardChangeParameter, history: any) {
  try {
    let updatedSpeakerName: string | null = null;
    const transcription = transcriptionManager.GetTranscription();

    // Don't update speaker name if it's being set to the same name we are displaying.
    if (data.speaker != GetSpeakerName(transcription.transcriptSegments[data.id].speakerId, transcription.speakerNames)) {
      updatedSpeakerName = data.speaker;
    }

    await transcriptionManager.UpdateTranscriptionSegment(data.id, data.text, updatedSpeakerName, data.changeAllSpeakers);
    InitializeTranscriptionData(history);
  } catch (error) {
    handleFailure(
      LogEventNames.DocumentStorage,
      GetFailureReasonForTranscribingError(error), 
      FailureContinuation.None /*failureContinuation*/, 
      'TranscriptionData update threw exception: ' + ErrorStringFromException(error),
      history);
  }
}

export async function AddTranscriptionToDocument(transcriptionManager: ITranscriptionManager, history: any, sourceFile: ITranscriptionSourceFile | null, addOption: AddToDocumentMenuOption) {
  try {
    await AddToDocument.AddTranscriptionToDocument(transcriptionManager.GetTranscription(), sourceFile, getAddToDocumentOptionsFromMenuOption(addOption));
  } catch(error) {
    handleFailure(
      LogEventNames.DocumentStorage,
      GetFailureReasonForTranscribingError(error), 
      FailureContinuation.None /*failureContinuation*/, 
      'AddTranscriptionToDocument threw exception: ' + ErrorStringFromException(error),
      history);
  }
}

export function AddTranscriptionSegmentToDocument(transcriptionManager: ITranscriptionManager, id: number, history: any) {
  try {
    AddToDocument.AddTranscriptionSegmentToDocument(transcriptionManager, id);
  } catch(error) {
    handleFailure(
      LogEventNames.DocumentStorage,
      GetFailureReasonForTranscribingError(error), 
      FailureContinuation.None /*failureContinuation*/, 
      'AddTranscriptionSegmentToDocument threw exception: ' + ErrorStringFromException(error),
      history);
  }
}

export function SetTranscriptionManager(transcriptionManager: ITranscriptionManager) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetTranscriptionManager,
    payload: transcriptionManager
  });
}

function handleFailure(area: LogEventNames, failureReason: string, continuation: FailureContinuation, failureReasonDebug: string, history: any) {
  LogError(area, failureReasonDebug);
  SetFailureStateAndRouteToFailure(area, failureReason, continuation, history);
}